import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { apiClient, type ResponseBody } from '../client';
import { queryKeys } from '../react-query.config';
import { toast } from '@/hooks/use-toast';

export type ReservePickemResponse = ResponseBody<'/api/pickems/{id}/reserve', 'post'>;

/**
 * Reserve a seat (DKPLAT-528) — POST /pickems/:id/reserve.
 * Follows the same conventions as useSubmitSelections: refetch the pickem
 * detail + all pickem lists on success so has_reservation/entry counters
 * refresh everywhere (lobby, upcoming, contest detail).
 */
export const useReservePickem = ({
  pickemId,
  onSuccess,
  showToast = false,
}: {
  pickemId: string;
  onSuccess?: (data: ReservePickemResponse) => void;
  showToast?: boolean;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['reservePickem', pickemId],
    mutationFn: async () => {
      return apiClient.post('/api/pickems/{id}/reserve', undefined, {
        pathParams: { id: pickemId },
      });
    },
    onSuccess: async data => {
      // Refetch the specific pickem detail
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.detail(pickemId),
      });

      // Refetch (not just invalidate) all pickems so has_reservation and entry
      // counters are up to date across lobby/upcoming — mirrors useSubmitSelections.
      await queryClient.refetchQueries({
        queryKey: queryKeys.pickem.all,
      });

      if (showToast) {
        toast({
          title: 'Success',
          description: "You're in! Don't forget to come back and make your picks.",
        });
      }

      onSuccess?.(data as ReservePickemResponse);
    },
    onError: (error: any) => {
      const backendError = (error.originalError as AxiosError)?.response?.data as {
        error?: string;
        message?: string;
      };

      if (error.statusCode === 409 && backendError?.error === 'ALREADY_ENTERED') {
        toast({
          title: 'Already entered',
          description: 'You already have an entry or reservation for this contest.',
          variant: 'destructive',
        });
        return;
      }

      if (error.statusCode === 400 && backendError?.error === 'CONTEST_CLOSED') {
        toast({
          title: 'Contest closed',
          description: 'This contest is no longer accepting entries.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to reserve a seat. Please try again.',
        variant: 'destructive',
      });
    },
  });

  return mutation;
};
